<template>
  <div class="ticket-list">
    <a-spin :spinning="loading">
      <template v-if="safeTickets.length">
        <div class="tickets-grid">
          <div
            v-for="ticket in safeTickets"
            :key="ticket.ticketNumber"
            class="ticket-card-wrapper"
            :class="{ 'is-disabled': disabled || ticket.disabled }"
            @click="selectTicket(ticket)"
          >
            <div class="ticket-card">
              <div class="ticket-header">
                <span class="ticket-number">{{ $t('tickets.ticketLabel', { number: ticket.ticketNumber }) }}</span>
                
                <div v-if="ticket.stats" class="result-badge" :class="getResultClass(ticket.stats)">
                  {{ getResultText(ticket.stats) }}
                </div>
              </div>
              
              <div class="test-range">
                <span class="range-label">{{ $t('tickets.testRange') }}</span>
                <span class="range-value">
                  {{ ticket.start_question_order || 1 }} - {{ ticket.end_question_order || (ticket.question_count || 20) }}
                </span>
              </div>
              
              <div v-if="ticket.stats" class="stats-container">
                <div class="stat-item success">
                  <span class="stat-value">{{ ticket.stats.correct || 0 }}</span>
                  <span class="stat-label">{{ $t('tickets.correctLabel') }}</span>
                </div>
                <div class="stat-item danger">
                  <span class="stat-value">{{ ticket.stats.wrong || 0 }}</span>
                  <span class="stat-label">{{ $t('tickets.wrongLabel') }}</span>
                </div>
                <div class="stat-item warning">
                  <span class="stat-value">{{ ticket.stats.skipped || 0 }}</span>
                  <span class="stat-label">{{ $t('tickets.skippedLabel') }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-else>
        <div class="empty-state">
          <a-empty :description="$t('tickets.notFoundEmpty')" />
        </div>
      </template>
    </a-spin>
  </div>
</template>

<script>
import { message } from 'ant-design-vue'

export default {
  name: 'TicketList',

  props: {
    tickets: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },

  emits: ['select'],

  computed: {
    safeTickets() {
      return Array.isArray(this.tickets) ? this.tickets : []
    }
  },

  methods: {
    selectTicket(ticket) {
      if (this.disabled || ticket.disabled) {
        message.warning(this.$t('tickets.buyPremium', 'Sizda Premium obuna mavjud emas. Iltimos, Premium xarid qiling!'));
        return;
      }
      this.$emit('select', ticket)
    },

    getResultClass(stats) {
      if (!stats) return ''
      
      const total = (stats.correct || 0) + (stats.wrong || 0) + (stats.skipped || 0)
      const correctPercent = ((stats.correct || 0) / total) * 100
      
      if (correctPercent >= 80) return 'excellent'
      if (correctPercent >= 60) return 'good'
      if (correctPercent >= 40) return 'medium'
      return 'bad'
    },

    getResultText(stats) {
      if (!stats) return ''
      
      const total = (stats.correct || 0) + (stats.wrong || 0) + (stats.skipped || 0)
      const correctPercent = Math.round(((stats.correct || 0) / total) * 100)
      
      if (correctPercent >= 80) return 'A’lo'
      if (correctPercent >= 60) return 'Yaxshi'
      if (correctPercent >= 40) return 'Qoniqarli'
      return 'Qoniqarsiz'
    }
  }
}
</script>

<style scoped>
.ticket-list {
  width: 100%;
}

.tickets-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px;
}

.ticket-card-wrapper {
  cursor: pointer;
  transition: all 0.2s ease;
}

.ticket-card-wrapper:hover {
  transform: translateY(-2px);
}

.ticket-card {
  background: var(--card-bg);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 16px;
  transition: all 0.3s ease;
  height: 100%;
}

.ticket-card-wrapper:hover .ticket-card {
  border-color: var(--primary);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.ticket-card-wrapper.is-disabled {
  opacity: 0.6;
  filter: grayscale(100%);
  cursor: not-allowed;
}

.ticket-card-wrapper.is-disabled:hover {
  transform: none;
}

.ticket-card-wrapper.is-disabled:hover .ticket-card {
  border-color: var(--border);
  box-shadow: none;
}

.ticket-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;
}

.ticket-number {
  font-size: 16px;
  font-weight: 600;
  color: var(--text);
}

.result-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
}

.result-badge.excellent {
  background: rgba(34, 197, 94, 0.2);
  color: #22c55e;
}

.result-badge.good {
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
}

.result-badge.medium {
  background: rgba(245, 158, 11, 0.2);
  color: #f59e0b;
}

.result-badge.bad {
  background: rgba(239, 68, 68, 0.2);
  color: #ef4444;
}

.test-range {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;
  padding: 8px 0;
  border-bottom: 1px dashed var(--border);
  flex-wrap: wrap;
}

.range-label {
  font-size: 13px;
  color: var(--text-soft);
}

.range-value {
  font-size: 15px;
  font-weight: 600;
  color: var(--primary);
  background: rgba(59, 130, 246, 0.1);
  padding: 2px 10px;
  border-radius: 20px;
}

.stats-container {
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 8px 0;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  gap: 8px;
  flex-wrap: wrap;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  flex: 1;
  min-width: 50px;
}

.stat-value {
  font-size: 18px;
  font-weight: 700;
}

.stat-label {
  font-size: 11px;
  color: var(--text-soft);
}

.stat-item.success .stat-value {
  color: #22c55e;
}

.stat-item.danger .stat-value {
  color: #ef4444;
}

.stat-item.warning .stat-value {
  color: #f59e0b;
}

.empty-state {
  padding: 48px 0;
  text-align: center;
}

.empty-state :deep(.ant-empty-description) {
  color: var(--text-soft);
}

/* Desktop (1024px dan katta) */
@media (min-width: 1200px) {
  .tickets-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

/* Tablet (768px - 1024px) */
@media (max-width: 1024px) {
  .tickets-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }
}

/* Mobile (576px - 768px) */
@media (max-width: 768px) {
  .tickets-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 14px;
  }
  
  .ticket-card {
    padding: 14px;
  }
  
  .ticket-number {
    font-size: 14px;
  }
  
  .range-value {
    font-size: 13px;
  }
  
  .stat-value {
    font-size: 16px;
  }
  
  .stat-label {
    font-size: 10px;
  }
  
  .result-badge {
    font-size: 10px;
    padding: 3px 6px;
  }
  
  .test-range {
    margin-bottom: 12px;
  }
}

/* Small Mobile (320px - 576px) */
@media (max-width: 576px) {
  .tickets-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .ticket-card {
    padding: 14px;
  }
  
  .ticket-header {
    margin-bottom: 10px;
  }
  
  .ticket-number {
    font-size: 15px;
  }
  
  .range-value {
    font-size: 14px;
  }
  
  .stats-container {
    justify-content: space-between;
  }
  
  .stat-item {
    min-width: 60px;
  }
  
  .stat-value {
    font-size: 17px;
  }
}

/* Extra small (320px dan kichik) */
@media (max-width: 380px) {
  .ticket-card {
    padding: 12px;
  }
  
  .ticket-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .result-badge {
    align-self: flex-start;
  }
  
  .test-range {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
  
  .stats-container {
    flex-direction: column;
    gap: 8px;
  }
  
  .stat-item {
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
    gap: 8px;
  }
}

/* Loading styles */
:deep(.ant-spin) {
  color: var(--primary);
}

:deep(.ant-spin-dot i) {
  background-color: var(--primary);
}

/* Animation */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.ticket-card-wrapper {
  animation: fadeIn 0.3s ease forwards;
}
</style>